import {
  ClickAwayListener,
  Drawer,
  PaperProps,
  Tab,
  Tabs,
  IconButton,
  ButtonBase,
} from '@mui/material';
import { useEffect } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { AppState, AppDispatch } from '../store';
import {
  getSelectableSections,
  isChildOf,
  getProductId,
  UrlHelper,
  getInitialTab,
  getLabelWithInCompleteIcon,
} from '../services';
import { ESectionInfoType, ESessionStore, EUrlParams } from '../data/Constants';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { SessionStore } from '../services/SessionStore';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import {
  setActiveTab,
  setApplicationSettings,
  setCurrentActiveTab,
} from '../store/states/ApplicationSettingsSlice';
import type { IApplicationSettings, IExtendedConfigureResponse, ISection } from '../../types';

const StyleConstants = {
  extraSmallHeaderHeight: '89px',
  headerHeight: '105px',
};

/**
 * Returns the PaperProps sx styling for the Drawer based on application settings.
 * @param {IApplicationSettings} applicationSettings - The application settings
 * @returns {object} The sx style object for PaperProps
 */
const getSXPaperProps = (applicationSettings: IApplicationSettings) => {
  return {
    position: applicationSettings.pageSize.isSmall ? 'absolute' : 'initial',
    transition: '.15s',
    boxShadow: applicationSettings.pageSize.isSmall ? '8px 0 8px -8px black' : 'none',
    top: '0',
    bottom: '0',
    height: applicationSettings.pageSize.isSmall ? 'unset' : '100%',
  };
};
/**
 * Determines if the section tabs should be shown based on application settings.
 * @param {IApplicationSettings} applicationSettings - The application settings
 * @returns {boolean} True if tabs should be shown, false otherwise
 */
function showTab(applicationSettings: IApplicationSettings) {
  return (
    (!applicationSettings.pageSize.isSmall && applicationSettings.showSectionTabs) ||
    (applicationSettings.pageSize.isSmall && applicationSettings.forceShowSectionTabs)
  );
}

/**
 * Returns the section icon (e.g., guarded icon) for a tab.
 * @param {ISection} section - Section info
 * @returns {React.JSX.Element | null} The icon or null
 */
function getTabIcon(section: ISection): React.JSX.Element | null {
  let tabIcon = null;
  if (section.id === ESectionInfoType.Guarded) {
    tabIcon = <LockOutlinedIcon />;
  }
  return tabIcon;
}

// Set the product wise active tab and current active tab if current model not exists in the productsActiveTab state
/**
 * Sets the default active tab for the product or submodel if not already set.
 * @param {ISection[]} selectableSections - The selectable sections
 * @param {IApplicationSettings} applicationSettings - The application settings
 * @param {string | null} modelPath - The model path
 * @param {string} productModel - The product model id
 * @param {AppDispatch} dispatch - Redux dispatch
 */
function setDefaultActiveTab(
  selectableSections: ISection[],
  applicationSettings: IApplicationSettings,
  modelPath: string | null,
  productModel: string,
  dispatch: AppDispatch
) {
  if (selectableSections.length > 0) {
    const initialTab = getInitialTab(selectableSections);

    if (modelPath && !applicationSettings.productsActiveTab[modelPath]) {
      //set the current active tab for submodels
      dispatch(
        setActiveTab({
          modelId: modelPath,
          sectionID: selectableSections[initialTab].id,
          tabIndex: initialTab,
        })
      );
      dispatch(setCurrentActiveTab({ activeTab: initialTab }));
    } else if (!applicationSettings.productsActiveTab[productModel]) {
      //set the current active tab for root model
      dispatch(
        setActiveTab({
          modelId: productModel,
          sectionID: selectableSections[initialTab].id,
          tabIndex: initialTab,
        })
      );
      dispatch(setCurrentActiveTab({ activeTab: initialTab }));
    }
  }
}

/**
 * Handles tab change logic and updates the active tab in the store.
 * @param {string} selectedSectionID - The selected section id
 * @param {number} value - The tab index
 * @param {string} tabID - The tab id
 * @param {string} productModel - The product model id
 * @param {string | null} modelPath - The model path
 * @param {AppDispatch} dispatch - Redux dispatch
 */
function changeTab(
  selectedSectionID: string,
  value: number,
  tabID: string,
  productModel: string,
  modelPath: string | null,
  dispatch: AppDispatch
) {
  if (modelPath) {
    dispatch(setActiveTab({ modelId: modelPath, sectionID: tabID, tabIndex: value }));
  } else {
    dispatch(setActiveTab({ modelId: productModel, sectionID: tabID, tabIndex: value }));
  }
  SessionStore.set(ESessionStore.IsGuardedTab, selectedSectionID === ESectionInfoType.Guarded);
}

/**
 * Returns the rendered Tab components for the selectable sections.
 * @param {IExtendedConfigureResponse} configuration - The configuration object
 * @param {ISection[]} selectableSections - The selectable sections
 * @param {number} tabIndex - The starting tab index
 * @returns {React.ReactNode[]} The array of Tab components
 */
function tabs(
  configuration: IExtendedConfigureResponse,
  selectableSections: ISection[],
  tabIndex: number
) {
  if (configuration.data?.sections) {
    return selectableSections?.map((s: ISection) => {
      const hideSection = s.isItemsAvailable ? 'd-none' : '';
      return (
        <Tab
          key={`${s.id}-${tabIndex}`}
          id={s.id}
          label={
            <div className=" d-flex align-center alignself-start">
              {getLabelWithInCompleteIcon(s.name ?? '', s.isComplete ?? true)} {getTabIcon(s)}
            </div>
          }
          value={tabIndex++}
          className={
            s.isSearchValueMatched
              ? `tab-drawer-SearchValueMatched ${hideSection}`
              : `tab-drawer-tabs ${hideSection}`
          }
        />
      );
    });
  }
  return [];
}

/**
 * Renders the content of the tab drawer once data is available.
 * This component contains the hooks and logic, and assumes its props are always defined.
 * @param {object} props - The component props
 * @param {IExtendedConfigureResponse} props.configuration - The configuration object
 * @param {IApplicationSettings} props.applicationSettings - The application settings
 * @returns {JSX.Element} The rendered tab drawer content
 */
const TabDrawerContent = ({
  configuration,
  applicationSettings,
}: {
  configuration: IExtendedConfigureResponse;
  applicationSettings: IApplicationSettings;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const productModel = getProductId();
  const modelPath = UrlHelper.getSearchParameter(EUrlParams.Model);
  const show = showTab(applicationSettings);
  const { sections: selectableSections } = getSelectableSections(configuration);
  const tabIndex = 0;
  /**
   * Handles tab change event.
   * @param {React.SyntheticEvent} _ - The event (unused)
   * @param {number} value - The new tab index
   */
  const onTabChange = (_: React.SyntheticEvent<Element, Event>, value: number) => {
    dispatch(setCurrentActiveTab({ activeTab: value }));
    changeTab(
      selectableSections[value]?.id,
      value,
      tabs(configuration, selectableSections, tabIndex)[value].props.id,
      productModel,
      modelPath,
      dispatch
    );
  };

  /**
   * Handles click away event to close the drawer on small screens.
   * @param {MouseEvent | TouchEvent} e - The event
   */
  const onClickAway = (e: MouseEvent | TouchEvent) =>
    !isChildOf(e.target as HTMLElement, 'tabDrawerToggle') &&
    applicationSettings.forceShowSectionTabs &&
    dispatch(setApplicationSettings({ forceShowSectionTabs: false }));

  const paperProps: Partial<PaperProps<'div', unknown>> = {
    sx: getSXPaperProps(applicationSettings),
    className: classNames('w-0', { ['drawerOpen']: show }),
  };

  const DrawerHeader = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
  }));

  // Hooks are now called unconditionally within this component
  useEffect(() => {
    setDefaultActiveTab(selectableSections, applicationSettings, modelPath, productModel, dispatch);
  }, [configuration.isDataLoaded, applicationSettings.productsActiveTab]);

  useEffect(() => {
    if (configuration.search.completed) {
      const index = selectableSections.findIndex((s) => s.isSearchValueMatched === true);
      SessionStore.set(
        ESessionStore.IsGuardedTab,
        selectableSections[index]?.id === ESectionInfoType.Guarded
      );
      dispatch(
        setCurrentActiveTab({ activeTab: index === -1 ? applicationSettings.activeTab : index })
      );
    }
  }, [configuration.search.completed]);

  /**
   * Handles toggling the drawer open/close state.
   */
  const onToggleDrawer = () => {
    if (applicationSettings.pageSize.isSmall) {
      dispatch(
        setApplicationSettings({
          forceShowSectionTabs: !applicationSettings.forceShowSectionTabs,
          forceShowSummary: false,
        })
      );
    } else {
      dispatch(
        setApplicationSettings({
          showSectionTabs: !applicationSettings.showSectionTabs,
        })
      );
    }
  };

  return (
    <>
      {show ? (
        <ClickAwayListener onClickAway={onClickAway}>
          <Drawer
            variant="permanent"
            open
            className="h-100"
            slotProps={{
              paper: paperProps,
            }}
          >
            <DrawerHeader>
              <IconButton onClick={onToggleDrawer} className="left-arrow" data-testid="left-arrow">
                <KeyboardDoubleArrowLeftIcon className={'pinIcon'} />
              </IconButton>
            </DrawerHeader>
            <Tabs
              value={applicationSettings.activeTab}
              onChange={onTabChange}
              orientation="vertical"
              variant="scrollable"
              scrollButtons={false}
              visibleScrollbar
            >
              {tabs(configuration, selectableSections, tabIndex)}
            </Tabs>
          </Drawer>
        </ClickAwayListener>
      ) : (
        <div className="tabDrawerMini">
          <IconButton id="tabDrawerToggle" onClick={onToggleDrawer}>
            <KeyboardDoubleArrowRightIcon className={'pinIcon'} />
          </IconButton>
          <ButtonBase
            component="span"
            className="navigateTabLabel rotate180"
            onClick={onToggleDrawer}
          >
            {t('tabSection.navigation')}
          </ButtonBase>
        </div>
      )}
    </>
  );
};

/**
 * Renders the section tabs on the left side on the home screen.
 * Gets automatically hidden on smaller screens.
 * @returns {JSX.Element | null} The tab drawer component or null if data is not ready
 */
export const TabDrawer = () => {
  const applicationSettings = useSelector((state: AppState) => state.applicationSettings);
  const configuration = useSelector((state: AppState) => state.configuration);

  // Early return if data is not available. This is now safe as no hooks are called afterward.
  if (!configuration || !applicationSettings) {
    return null;
  }

  // Render the content component and pass the guaranteed data as props
  return (
    <TabDrawerContent configuration={configuration} applicationSettings={applicationSettings} />
  );
};
